import { actorPuedeOperarSobre, esSelf, esUltimoSuperAdmin } from './role-hierarchy';
import { NIVEL_SUPER_ADMIN } from './usuario.entity';

import type { Rol, SessionUser } from './usuario.entity';

export type Action = 'read' | 'create' | 'update' | 'delete';

export type Resource =
  | 'usuarios'
  | 'proyectos'
  | 'catalogos'
  | 'incidencias'
  | 'bitacora'
  | 'reportes';

/**
 * Contexto opcional para reglas dependientes del target o del entorno.
 *
 *  - `targetRol`: rol del usuario sobre el que se quiere actuar. Necesario
 *    para `assertHierarchyAbove` espejado en el frontend.
 *  - `targetUserId`: id del usuario target. Se usa para `assertNotSelf`.
 *  - `superAdminsActivos`: cuántos SuperAdmin `Activo` hay. La UI debe
 *    pasarlo (cache o query) para no permitir desactivar / degradar al
 *    último.
 *  - `proyectoId` y compañía: ABAC heredado (lectura por proyecto).
 */
export type AccessContext = {
  targetRol?: Rol | null;
  targetUserId?: number | null;
  superAdminsActivos?: number;
  proyectoId?: string;
  subfaseId?: string;
  hiloId?: string;
};

/** Permiso "wildcard" del SuperAdmin. */
const WILDCARD = '*';

/**
 * Lista de permisos efectivos (al menos uno) que satisfacen un
 * `(resource, action)`. La fuente de verdad son los strings que el
 * backend declara con `@Permissions(...)` en `prisma/rbac-catalog.ts`
 * (ADR-038 del backend: el permiso manda, granularidad CRUD). El test de
 * contrato (`backend-permissions.mirror.test.ts`) garantiza que nada de lo
 * que se retorna aquí quede fuera del catálogo.
 *
 * Las capabilities fuera del CRUD canónico (`proyectos:iniciar`,
 * `proyectos:asignar-*`, `incidencias:aprobar`, …) no entran al switch: se
 * verifican con `hasPermission`/`hasAnyPermission` por string exacto desde
 * la feature correspondiente.
 */
function permisosRequeridos(resource: Resource, action: Action): string[] {
  switch (resource) {
    case 'bitacora':
      // La bitácora del sistema (audit_log) viaja por la reportería de
      // gestión/seguridad del backend (ADR-022 D3). Sin permiso propio.
      return ['reportes:gestion'];
    case 'usuarios':
      if (action === 'read') return ['usuarios:listar'];
      if (action === 'create') return ['usuarios:crear'];
      if (action === 'update') return ['usuarios:actualizar'];
      return ['usuarios:eliminar'];
    case 'catalogos':
      if (action === 'read') return ['catalogos-proyectos:listar', 'catalogos-proyectos:gestionar'];
      return ['catalogos-proyectos:gestionar'];
    case 'proyectos':
      if (action === 'read') return ['proyectos:listar'];
      if (action === 'create') return ['proyectos:crear'];
      if (action === 'update') return ['proyectos:actualizar'];
      return ['proyectos:eliminar'];
    case 'incidencias':
      // Lectura con visibilidad por proyecto/rol resuelta por el backend.
      // Las mutaciones del workflow no tienen verbo CRUD canónico: cada
      // feature verifica su permiso específico (`incidencias:aprobar`, …);
      // el genérico de mutación solo cubre "puede reportar".
      if (action === 'read') return ['incidencias:listar'];
      return ['incidencias:reportar'];
    case 'reportes':
      // Un permiso por frente (ADR-022/030). Cualquiera habilita el módulo;
      // la visibilidad por pestaña se afina con `hasPermission` por frente.
      // Dos excepciones, ambas permisos por REPORTE y no por frente:
      //  - `reportes:incidencias-estadisticas` (ADR-040): vista de estadísticas
      //    parametrizables (paquete base de Supervisor/Admin; delegable).
      //  - `reportes:incidencias-detalle` (ADR-049): expediente completo. Lo
      //    tienen TODOS los roles que ven incidencias, el Participante Privado
      //    y el Solicitante incluidos, porque el backend redacta el contenido
      //    según el rol de quien lo pide. Es lo que abre `/reportes` para esos
      //    actores externos, que no tienen ningún frente agregado.
      // Tener un frente NO incluye estos dos, ni al revés.
      return [
        'reportes:incidencias',
        'reportes:cumplimiento',
        'reportes:gestion',
        'reportes:incidencias-estadisticas',
        'reportes:incidencias-detalle',
      ];
  }
}

/**
 * ¿El usuario tiene el permiso `p` en sus permisos efectivos?
 *
 * El backend ya resuelve `(Rol.permisos ∪ Perfil.permisos ∪ grants) − revokes`
 * y lo entrega en `SessionUser.permisos`. El wildcard `'*'` (exclusivo del
 * paquete base de SuperAdmin) satisface cualquier permiso.
 */
export function hasPermission(
  user: Pick<SessionUser, 'permisos'> | null | undefined,
  permiso: string,
): boolean {
  if (!user) return false;
  if (user.permisos.includes(WILDCARD)) return true;
  return user.permisos.includes(permiso);
}

/**
 * Variante any-of: ¿el usuario tiene AL MENOS UNO de los permisos? Útil
 * para gates duales durante la transición ADR-038 (permiso nuevo OR
 * permiso viejo) y para acciones que aceptan más de una capability.
 */
export function hasAnyPermission(
  user: Pick<SessionUser, 'permisos'> | null | undefined,
  ...permisos: string[]
): boolean {
  return permisos.some((p) => hasPermission(user, p));
}

/**
 * RBAC + ABAC + jerarquía. Función pura. Espeja las reglas del backend
 * (`AuthzPolicyService`, ADR-015 §6). El backend sigue siendo la fuente
 * de verdad: esto es defensa en profundidad para ocultar/deshabilitar
 * acciones antes del 403.
 *
 * Regla cruzada R5 del ARCHITECTURE: `bitacora` es solo lectura desde el
 * frontend, sin importar el rol.
 *
 * Reglas evaluadas en orden:
 *  1. Sin usuario → false.
 *  2. Bitácora + mutación → false (R5).
 *  3. Sin el permiso efectivo requerido → false.
 *  4. Sobre un target con rol presente: actor.nivel debe ser mayor.
 *  5. Acciones sobre uno mismo (cambiar-rol, asignar-perfil, desactivar)
 *     están prohibidas (`assertNotSelf` del backend).
 *  6. Si el target es el último SuperAdmin y la acción cambiaría su rol o
 *     estado → false (`assertNotLastSuperAdmin`).
 *  7. ABAC: lectura por `proyectoId`, cuando el SessionUser limita su
 *     visibilidad. Si el usuario no declara `proyectos` en su shape, la
 *     lectura no se restringe a nivel cliente (el backend filtra).
 */
export function canAccess(
  user: SessionUser | null | undefined,
  resource: Resource,
  action: Action = 'read',
  context?: AccessContext,
): boolean {
  if (!user) return false;

  // R5 universal: bitácora es solo lectura desde el frontend.
  if (resource === 'bitacora' && action !== 'read') return false;

  if (!permisosRequeridos(resource, action).some((p) => hasPermission(user, p))) return false;

  // SuperAdmin bypass: acceso total a self + cualquier jerarquía. Único
  // límite que conserva es `esUltimoSuperAdmin` (continuidad del sistema).
  const isMutation = action !== 'read';
  const actorEsSuperAdmin = user.rol.nivel === NIVEL_SUPER_ADMIN;

  if (isMutation && context) {
    if (
      !actorEsSuperAdmin &&
      context.targetRol &&
      !actorPuedeOperarSobre(user, context.targetRol)
    ) {
      return false;
    }
    if (!actorEsSuperAdmin && esSelf(user.id, context.targetUserId ?? null)) {
      return false;
    }
    if (
      typeof context.superAdminsActivos === 'number' &&
      esUltimoSuperAdmin(context.targetRol ?? null, context.superAdminsActivos)
    ) {
      return false;
    }
  }

  // ABAC: el SessionUser no declara `proyectos` en su shape actual
  // (los proyectos asignados viven en otra agregación). Si en el futuro
  // se agrega `proyectos: string[]` al SessionUser, el filtro vive aquí.
  void context?.proyectoId;

  return true;
}
